import { type FC, useEffect } from 'react';

import { useMapFeaturesContext } from '@/microfrontends/map/shared/context/MapLayersCenterContext';
import { convertClusterToRBushItem } from '@/microfrontends/map/shared/utils/map/convertClusterToRBushItem';
import { getTilesToUpdate } from '@/microfrontends/map/shared/utils/map/tiles';

import { useGetClustersQuery } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/query';
import { clustersSelector } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/selectors';
import { useMapConfigurationSelector } from '@/packages/redux-state/store/slices/mapConfiguration';

import { useCurrentAreaSearchUpdateOnClustersChange } from './internal/hooks/useCurrentAreaSearchUpdateOnClustersChange';
import { useGetClustersOnMapOpen } from './internal/hooks/useGetClustersOnMapOpen';
import { useGetClustersOnSelectedBoundsChange } from './internal/hooks/useGetClustersOnSelectedBoundsChange';
import { useHoveredOfferPointer } from './internal/hooks/useHoveredOfferPointer';
import { useMapUpdateOnFiltersChange } from './internal/hooks/useMapUpdateOnFiltersChange';

/**
 * @description менеджер отвечает за обновление пинов на карте при изменении массива кластеров "clusters"
 */
export const MapPinsUpdater: FC = () => {
  const { rbush, pinsLayer, hotspotsLayer, balloonManager } = useMapFeaturesContext();
  const { bounds, zoom } = useMapConfigurationSelector();

  const { data: clusters, isFetching, isSuccess } = useGetClustersQuery({ select: clustersSelector });

  /**
   * Открываем карту - запрашиваем кластеры
   */
  useGetClustersOnMapOpen();

  /**
   * Меняется выбранная область (кнопка "Искать в этой области") - перезапрашиваем кластеры
   */
  useGetClustersOnSelectedBoundsChange();

  /**
   * При изменеии в фильтрах перезапрашиваем кластера, при изменении гео перезапрашиваем конфиг карты
   */
  useMapUpdateOnFiltersChange();

  /**
   * При обновлении кластеров обновляем состояние плашки "Искать в этой области"
   */
  useCurrentAreaSearchUpdateOnClustersChange();

  /**
   * При наведении на объявление в списке обновляем пин на карте
   */
  useHoveredOfferPointer();

  useEffect(() => {
    if (isFetching || !isSuccess) {
      return;
    }

    const rbushItems = clusters.map(convertClusterToRBushItem);

    rbush.clear();
    pinsLayer.layer.update();

    // В новом наборе кластеров нет данных
    if (!rbushItems.length) {
      balloonManager.removeBalloons();

      return;
    }

    rbush.load(rbushItems);

    // Ищем тайлы, которые требуют обновления, потому что в них есть новые пины
    const tilesToUpdate = getTilesToUpdate({ bounds, includeShadow: true, rbushItems, zoom });

    pinsLayer.layer.update(tilesToUpdate.length ? tilesToUpdate : undefined);
    balloonManager.updateBalloons(clusters);
    hotspotsLayer.layer.update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, isSuccess, balloonManager, hotspotsLayer.layer, pinsLayer.layer, rbush, clusters]);

  return null;
};
